// VGUI KeyValues, independent of DOM and materials. Preserve duplicate keys and
// source order: resources use repeated controls and platform-specific overrides.
const MAX_BYTES = 2 * 1024 * 1024;
export function parseResource(text, conditions = new Set(['win32', 'posix'])) {
    if (text.length > MAX_BYTES) throw new Error('VGUI resource size limit');
    const tokens = []; let pos = 0;
    while (pos < text.length) {
        const c = text[pos];
        if (/\s|\uFEFF/.test(c)) { pos++; continue; }
        if (text.startsWith('//', pos)) { pos = text.indexOf('\n', pos); if (pos < 0) break; continue; }
        if (text.startsWith('/*', pos)) {
            const end = text.indexOf('*/', pos + 2); if (end < 0) throw new Error('Unclosed VGUI comment');
            pos = end + 2; continue;
        }
        if ('{}'.includes(c)) { tokens.push(c); pos++; }
        else if (c === '"') {
            let value = ''; pos++; let closed = false;
            while (pos < text.length) {
                const ch = text[pos++];
                if (ch === '"') { closed = true; break; }
                if (ch === '\\' && pos < text.length && ['"', '\\', 'n', 't'].includes(text[pos])) {
                    const next = text[pos++]; value += next === 'n' ? '\n' : next === 't' ? '\t' : next;
                } else value += ch;
            }
            if (!closed) throw new Error('Unclosed VGUI string');
            tokens.push({ value, quoted: true });
        } else {
            const start = pos; while (pos < text.length && !/[\s{}"]/.test(text[pos])) pos++;
            tokens.push({ value: text.slice(start, pos) });
        }
        if (tokens.length > 100000) throw new Error('VGUI token limit');
    }
    pos = 0;
    function block(depth, nested) {
        if (depth > 32) throw new Error('VGUI nesting limit');
        const result = [];
        while (pos < tokens.length && tokens[pos] !== '}') {
            const key = tokens[pos++]; if (!key?.value) throw new Error('Invalid VGUI key');
            let enabled = true;
            const conditional = () => {
                while (!tokens[pos]?.quoted && tokens[pos]?.value?.startsWith('[')) {
                    const condition = tokens[pos++].value;
                    const match = /^\[(!?)\$([\w]+)\]$/.exec(condition);
                    if (!match) throw new Error('Unsupported VGUI condition');
                    const present = conditions.has(match[2].toLowerCase());
                    enabled &&= match[1] ? !present : present;
                }
            };
            // Valve uses both "Panel" [$X360] { ... } and scalar postfixes.
            conditional();
            const token = tokens[pos++];
            let value;
            if (token === '{') value = block(depth + 1, true);
            else if (typeof token?.value === 'string') value = token.value;
            else throw new Error('Incomplete VGUI entry');
            conditional();
            if (enabled) result.push({ key: key.value.toLowerCase(), value });
        }
        if (nested && tokens[pos++] !== '}') throw new Error('Unclosed VGUI block');
        return result;
    }
    const result = block(0, false);
    if (pos !== tokens.length) throw new Error('Unexpected VGUI closing brace');
    return result;
}
export const resourceValue = (entries, key) => entries?.findLast(entry => entry.key === key.toLowerCase())?.value;
export function resourceProperties(entries, conditions = new Set()) {
    const result = Object.create(null);
    for (const { key, value } of entries ?? []) {
        if (!Array.isArray(value)) result[key] = value;
        else if (key.startsWith('if_') && conditions.has(key.slice(3))) Object.assign(result, resourceProperties(value, conditions));
    }
    return result;
}
export function resourcePath(path, parent = '') {
    path = path.replaceAll('\\', '/').toLowerCase();
    if (!path.startsWith('resource/') && path !== 'scripts/hudlayout.res') path = parent.slice(0, parent.lastIndexOf('/') + 1) + path;
    if ((!path.startsWith('resource/') && path !== 'scripts/hudlayout.res') || !path.endsWith('.res') || path.length > 512 || /[:\x00-\x1f]/.test(path)
        || path.split('/').some(part => !part || part === '.' || part === '..')) throw new Error('Invalid VGUI resource path');
    return path;
}
function inherit(local, base) {
    const merged = local.map(entry => ({ ...entry }));
    for (const entry of base) {
        const existing = merged.find(candidate => candidate.key === entry.key);
        if (!existing) merged.push(entry);
        else if (Array.isArray(existing.value) && Array.isArray(entry.value)) existing.value = inherit(existing.value, entry.value);
    }
    return merged;
}
export async function readResource(path, load, { conditions = new Set(['win32', 'posix']) } = {}, chain = [], budget = { files: 0, bytes: 0 }) {
    path = resourcePath(path);
    if (chain.length >= 16 || chain.includes(path)) throw new Error('Cyclic VGUI resource include');
    if (++budget.files > 64) throw new Error('VGUI include count limit');
    const bytes = await load(path);
    budget.bytes += bytes.byteLength;
    if (budget.bytes > 8 * MAX_BYTES) throw new Error('VGUI include size limit');
    if (bytes.byteLength > MAX_BYTES) throw new Error('VGUI resource size limit');
    const entries = parseResource(new TextDecoder('utf-8', { fatal: true }).decode(bytes), conditions);
    let result = entries.filter(entry => !['#base', '#include'].includes(entry.key));
    for (const { key, value } of entries) if (['#base', '#include'].includes(key)) {
        if (typeof value !== 'string') throw new Error('Invalid VGUI include');
        const included = await readResource(resourcePath(value, path), load, { conditions }, [...chain, path], budget);
        result = key === '#base' ? inherit(result, included) : [...result, ...included];
    }
    return result;
}
// TF2 proportional coordinates use a 640x480 reference. c/r anchor against
// the parent, s scales the parent's size (e.g. cs-0.5 centers a whole panel).
export function resourceCoordinate(value, parent, fallback = 0) {
    const match = /^([cr]?)(s?)(-?\d+(?:\.\d+)?)$/i.exec(String(value ?? ''));
    if (!match) return fallback;
    const amount = Number(match[3]) * (match[2] ? parent : 1);
    return (match[1] === 'c' ? parent / 2 : match[1] === 'r' ? parent : 0) + amount;
}
